import type { RazorpayConfig, RazorpayResponse, RazorpayError } from '../types';

interface PaymentCallbacks {
  onSuccess: (response: RazorpayResponse) => void;
  onError: (error: RazorpayError) => void;
  onDismiss: () => void;
}

class RazorpayService {
  private currentInstance: any = null;

  async initiatePayment(config: RazorpayConfig, callbacks: PaymentCallbacks): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Clean up any existing instance
        this.cleanup();

        // Validate Razorpay is loaded
        if (!window.Razorpay) {
          throw new Error('Razorpay SDK not loaded');
        }

        console.log('🚀 Creating new Razorpay instance with config:', {
          amount: config.amount,
          order_id: config.orderId,
          key: config.key.substring(0, 10) + '...'
        });

        // Create Razorpay options following official documentation
        const options = {
          key: config.key,
          amount: config.amount,
          currency: config.currency || 'INR',
          order_id: config.orderId,
          name: config.name || 'Payment',
          description: config.description || 'Payment for services',
          prefill: config.prefill || {},
          theme: { color: '#3399cc' },
          
          // Success handler - called when payment succeeds
          handler: (response: RazorpayResponse) => {
            console.log('✅ Payment successful:', response);
            this.cleanup();
            callbacks.onSuccess(response);
            resolve();
          },
          
          // Modal configuration
          modal: {
            // Called when modal is dismissed without payment
            ondismiss: () => {
              console.log('❌ Payment modal dismissed');
              this.cleanup();
              callbacks.onDismiss();
              resolve();
            },
            escape: true,
            animation: true
          }
        };

        // Create and store instance
        this.currentInstance = new window.Razorpay(options);

        // Set up error handler using the proper event
        this.currentInstance.on('payment.failed', (response: { error: RazorpayError['error'] }) => {
          console.log('❌ Payment failed:', response.error);
          this.cleanup();
          callbacks.onError({ error: response.error });
          resolve();
        });

        // Open the payment modal
        console.log('🔓 Opening Razorpay modal');
        this.currentInstance.open();

      } catch (error) {
        console.error('💥 Failed to initiate payment:', error);
        this.cleanup();
        reject(error);
      }
    });
  }

  // Clean up current instance
  private cleanup(): void {
    if (this.currentInstance) {
      console.log('🧹 Cleaning up Razorpay instance');
      try {
        // Close modal if open
        this.currentInstance.close();
      } catch (e) {
        // Modal might already be closed
      }
      this.currentInstance = null;
    }
  }

  // Force close current payment modal
  forceClose(): void {
    console.log('🔒 Force closing Razorpay modal');
    this.cleanup();
  }

  // Check if payment modal is currently open
  isModalOpen(): boolean {
    return this.currentInstance !== null;
  }

  // Check if payment can be retried based on error
  canRetryPayment(error: { code: string; description: string; source: string; step: string; reason: string; metadata: Record<string, any> }): boolean {
    // Allow retry for most errors except user cancellation
    if (error.description?.toLowerCase().includes('cancelled') || 
        error.description?.toLowerCase().includes('cancel')) {
      return true; // User can retry after cancellation
    }
    
    // Allow retry for network/technical errors
    if (error.code?.includes('NETWORK_ERROR') || 
        error.code?.includes('GATEWAY_ERROR') ||
        error.description?.toLowerCase().includes('network')) {
      return true;
    }
    
    // Default to allowing retry
    return true;
  }
}

// Export singleton instance
export const razorpayService = new RazorpayService();